import { Button, Typography, Box, Alert, LinearProgress, Paper } from '@mui/material';
import { useState } from 'react';
import { Upload, Mail } from 'lucide-react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import { useCreateSubmission } from '@lingaai/shared-hooks';
import type { Tenant } from '@lingaai/types';

interface SubmitMetricsProps {
  companyId: string;
  tenant: Tenant;
  currentPeriod: string;
}

export function SubmitMetrics({ companyId, tenant, currentPeriod }: SubmitMetricsProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const supabase = getSupabaseClient();
  const createSubmission = useCreateSubmission();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Validate file type
      const validTypes = ['application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

      if (!validTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF or Excel file');
        return;
      }

      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Create submission record
      setStatus('Creating submission...');
      const submission = await createSubmission.mutateAsync({
        portfolio_company_id: companyId,
        period: currentPeriod,
        source_type: 'upload',
      });

      // 2. Upload file to storage
      setStatus('Uploading your report...');
      const timestamp = Date.now();
      const filePath = `${tenant.id}/submissions/${companyId}/${timestamp}-${file.name}`;

      const { error: uploadError } = await supabase.storage.from('portfolio-imports').upload(filePath, file);

      if (uploadError) throw uploadError;

      // 3. Create submission file record
      const { error: fileRecordError } = await supabase.from('submission_files').insert({
        submission_id: submission.id,
        tenant_id: tenant.id,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type,
      });

      if (fileRecordError) throw fileRecordError;

      // 4. Trigger processing (same as email pipeline)
      setStatus('Processing metrics...');

      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/process-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submission.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process submission');
      }

      setStatus('Complete!');
      setSuccess(true);
      setFile(null);
    } catch (err) {
      console.error('Submission upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Submit Metrics for {currentPeriod}
      </Typography>

      {/* Email Instructions */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Mail size={24} strokeWidth={1} color="currentColor" style={{ color: 'var(--mui-palette-primary-main)' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Preferred: Email Your Reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Simply CC your quarterly reports to:
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'background.paper',
                p: 1.5,
                borderRadius: 1,
                display: 'inline-block',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {tenant.workspace_email}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Our system will automatically process your submission and extract metrics.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* File Upload Alternative */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Or Upload Directly
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload your quarterly report (PDF or Excel) and we'll process it the same way.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your submission has been uploaded and is being processed. Check the History tab to track progress.
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <input type="file" accept=".pdf,.xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} id="metrics-file-upload" disabled={isProcessing} />
          <label htmlFor="metrics-file-upload">
            <Button variant="outlined" component="span" disabled={isProcessing} fullWidth startIcon={<Upload size={18} strokeWidth={1} />}>
              {file ? file.name : 'Choose File'}
            </Button>
          </label>
        </Box>

        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {status}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        <Button variant="contained" onClick={handleUpload} disabled={!file || isProcessing} fullWidth>
          {isProcessing ? 'Processing...' : 'Upload & Submit'}
        </Button>
      </Paper>
    </Box>
  );
}
